<div class="rp-page">

  <!-- Page header -->
  <div class="rp-page-header">
    <div>
      <h1 class="rp-title">Resource Planning</h1>
      <p class="rp-subtitle">Workforce allocation across all active projects</p>
    </div>
    <button mat-stroked-button (click)="load()">
      <mat-icon>refresh</mat-icon> Refresh
    </button>
  </div>

  <!-- Loading -->
  <div class="rp-loading" *ngIf="isLoading">
    <div class="css-spinner"></div>
    <p>Loading workforce data...</p>
  </div>

  <ng-container *ngIf="!isLoading">

    <!-- KPI cards -->
    <div class="rp-kpis">
      <div class="rp-kpi-card">
        <div class="rp-kpi-value">{{ activeProjects.length }}</div>
        <div class="rp-kpi-label">Active Projects</div>
      </div>
      <div class="rp-kpi-card rp-kpi-inactive" *ngIf="inactiveProjects.length > 0">
        <div class="rp-kpi-value">{{ inactiveProjects.length }}</div>
        <div class="rp-kpi-label">Inactive Projects</div>
      </div>
      <div class="rp-kpi-card">
        <div class="rp-kpi-value">{{ totalWorkersAcrossAllProjects }}</div>
        <div class="rp-kpi-label">Total Workers</div>
      </div>
      <div class="rp-kpi-card">
        <div class="rp-kpi-value">{{ totalAllHours | number:'1.0-0' }}</div>
        <div class="rp-kpi-label">Total Hours</div>
      </div>
      <div class="rp-kpi-card rp-kpi-warning" *ngIf="totalOvertimeWorkers > 0">
        <div class="rp-kpi-value">{{ totalOvertimeWorkers }}</div>
        <div class="rp-kpi-label">Workers with OT</div>
      </div>
      <div class="rp-kpi-card rp-kpi-cost">
        <div class="rp-kpi-value">{{ totalAllCost | currency:'USD':'symbol':'1.0-0' }}</div>
        <div class="rp-kpi-label">Total Labor Cost</div>
      </div>
    </div>

    <!-- View toggle -->
    <div class="rp-view-toggle">
      <button mat-button [class.active]="activeView === 'roster'" (click)="activeView = 'roster'">
        <mat-icon>people</mat-icon> Worker Roster
      </button>
      <button mat-button [class.active]="activeView === 'projects'" (click)="activeView = 'projects'">
        <mat-icon>business</mat-icon> By Project
      </button>
    </div>

    <!-- Worker Roster View -->
    <div *ngIf="activeView === 'roster'">
      <div class="rp-section-header">
        <h2 class="rp-section-title">Cross-Project Worker Roster</h2>
        <p class="rp-section-desc">Workers sorted by total hours. OT risk flagged for proactive scheduling.</p>
      </div>

      <div class="rp-empty" *ngIf="workerRoster.length === 0">
        <mat-icon>people_outline</mat-icon>
        <p>No labor entries found across active projects.</p>
        <p class="rp-empty-hint">Add labor entries to individual projects to see workforce data here.</p>
      </div>

      <div class="rp-table-wrap" *ngIf="workerRoster.length > 0">
        <table mat-table [dataSource]="workerRoster" class="rp-table">

          <ng-container matColumnDef="workerName">
            <th mat-header-cell *matHeaderCellDef>Worker</th>
            <td mat-cell *matCellDef="let w">
              <div class="rp-worker-cell">
                <div class="rp-avatar">{{ w.workerName[0] }}</div>
                <div>
                  <div class="rp-worker-name">{{ w.workerName }}</div>
                  <div class="rp-worker-trade">{{ w.trade }}</div>
                </div>
              </div>
            </td>
          </ng-container>

          <ng-container matColumnDef="trade">
            <th mat-header-cell *matHeaderCellDef>Trade</th>
            <td mat-cell *matCellDef="let w">{{ w.trade }}</td>
          </ng-container>

          <ng-container matColumnDef="projects">
            <th mat-header-cell *matHeaderCellDef>Projects</th>
            <td mat-cell *matCellDef="let w">
              <span class="rp-project-tag" *ngFor="let p of w.projects">{{ p }}</span>
              <span *ngIf="w.projects.length === 0" class="rp-no-project">—</span>
            </td>
          </ng-container>

          <ng-container matColumnDef="regularHours">
            <th mat-header-cell *matHeaderCellDef>Reg Hrs</th>
            <td mat-cell *matCellDef="let w">{{ w.regularHours | number:'1.1-1' }}</td>
          </ng-container>

          <ng-container matColumnDef="overtimeHours">
            <th mat-header-cell *matHeaderCellDef>OT Hrs</th>
            <td mat-cell *matCellDef="let w" [class.rp-ot-value]="w.overtimeHours > 0">
              {{ w.overtimeHours | number:'1.1-1' }}
            </td>
          </ng-container>

          <ng-container matColumnDef="totalHours">
            <th mat-header-cell *matHeaderCellDef>Total Hrs</th>
            <td mat-cell *matCellDef="let w"><strong>{{ w.totalHours | number:'1.1-1' }}</strong></td>
          </ng-container>

          <ng-container matColumnDef="totalCost">
            <th mat-header-cell *matHeaderCellDef>Cost</th>
            <td mat-cell *matCellDef="let w">{{ w.totalCost | currency }}</td>
          </ng-container>

          <ng-container matColumnDef="otRisk">
            <th mat-header-cell *matHeaderCellDef>OT Risk</th>
            <td mat-cell *matCellDef="let w">
              <span class="rp-risk-badge" [ngClass]="otRiskClass(w.otRisk)">
                <mat-icon *ngIf="w.otRisk === 'high'">warning</mat-icon>
                <mat-icon *ngIf="w.otRisk === 'medium'">info</mat-icon>
                <mat-icon *ngIf="w.otRisk === 'none'">check_circle</mat-icon>
                {{ w.otRisk | titlecase }}
              </span>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="workerColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: workerColumns;"
              [class.rp-row-ot-high]="row.otRisk === 'high'"
              [class.rp-row-ot-medium]="row.otRisk === 'medium'"></tr>
        </table>
      </div>
    </div>

    <!-- By-Project View -->
    <div *ngIf="activeView === 'projects'">
      <div class="rp-section-header">
        <h2 class="rp-section-title">Workforce by Project</h2>
        <p class="rp-section-desc">See who is working on each project and their labor costs.</p>
      </div>

      <!-- Active Projects -->
      <div class="rp-subsection-header">
        <mat-icon class="rp-subsection-icon rp-icon-active">check_circle</mat-icon>
        <h3 class="rp-subsection-title">Active Projects</h3>
        <span class="rp-subsection-count">{{ activeProjectSummaries.length }}</span>
      </div>

      <div class="rp-project-cards">
        <ng-container *ngFor="let summary of activeProjectSummaries">
          <div class="rp-project-card">
            <div class="rp-project-card-header">
              <div class="rp-project-name">{{ summary.project.title }}</div>
              <div class="rp-project-stats">
                <span><mat-icon>people</mat-icon> {{ summary.workerCount }}</span>
                <span><mat-icon>schedule</mat-icon> {{ summary.totalRegularHours | number:'1.0-0' }} hrs</span>
                <span class="rp-ot-warning" *ngIf="summary.totalOvertimeHours > 0">
                  <mat-icon>warning</mat-icon> {{ summary.totalOvertimeHours | number:'1.0-0' }} OT hrs
                </span>
                <span class="rp-cost-total"><mat-icon>attach_money</mat-icon> {{ summary.totalLaborCost | currency:'USD':'symbol':'1.0-0' }}</span>
              </div>
            </div>
            <div class="rp-no-workers" *ngIf="summary.workers.length === 0">No labor entries yet for this project.</div>
            <div class="rp-worker-list" *ngIf="summary.workers.length > 0">
              <div class="rp-worker-row" *ngFor="let w of summary.workers" [class.rp-worker-ot]="w.overtimeHours > 0">
                <div class="rp-mini-avatar">{{ w.workerName[0] }}</div>
                <div class="rp-worker-info">
                  <span class="rp-worker-name">{{ w.workerName }}</span>
                  <span class="rp-worker-trade">{{ w.trade }}</span>
                </div>
                <div class="rp-worker-hours">
                  {{ w.regularHours | number:'1.1-1' }} reg
                  <span *ngIf="w.overtimeHours > 0" class="rp-ot-hrs">+ {{ w.overtimeHours | number:'1.1-1' }} OT</span>
                </div>
                <div class="rp-worker-cost">{{ w.totalCost | currency }}</div>
              </div>
            </div>
          </div>
        </ng-container>

        <div class="rp-empty" *ngIf="activeProjectSummaries.length === 0">
          <mat-icon>business</mat-icon>
          <p>No active projects. Mark a project as Active to see it here.</p>
        </div>
      </div>

      <!-- Inactive Projects -->
      <div class="rp-subsection-header rp-subsection-header-inactive" *ngIf="inactiveProjectSummaries.length > 0">
        <mat-icon class="rp-subsection-icon rp-icon-inactive">pause_circle</mat-icon>
        <h3 class="rp-subsection-title rp-title-inactive">Inactive / Completed Projects</h3>
        <span class="rp-subsection-count rp-count-inactive">{{ inactiveProjectSummaries.length }}</span>
      </div>

      <div class="rp-project-cards" *ngIf="inactiveProjectSummaries.length > 0">
        <ng-container *ngFor="let summary of inactiveProjectSummaries">
          <div class="rp-project-card rp-project-card-inactive">
            <div class="rp-project-card-header rp-project-card-header-inactive">
              <div class="rp-project-name">{{ summary.project.title }}</div>
              <span class="rp-inactive-badge">Inactive</span>
              <div class="rp-project-stats">
                <span><mat-icon>people</mat-icon> {{ summary.workerCount }}</span>
                <span><mat-icon>schedule</mat-icon> {{ summary.totalRegularHours | number:'1.0-0' }} hrs</span>
                <span class="rp-cost-total"><mat-icon>attach_money</mat-icon> {{ summary.totalLaborCost | currency:'USD':'symbol':'1.0-0' }}</span>
              </div>
            </div>
            <div class="rp-no-workers" *ngIf="summary.workers.length === 0">No labor entries for this project.</div>
            <div class="rp-worker-list rp-worker-list-inactive" *ngIf="summary.workers.length > 0">
              <div class="rp-worker-row" *ngFor="let w of summary.workers">
                <div class="rp-mini-avatar rp-mini-avatar-inactive">{{ w.workerName[0] }}</div>
                <div class="rp-worker-info">
                  <span class="rp-worker-name">{{ w.workerName }}</span>
                  <span class="rp-worker-trade">{{ w.trade }}</span>
                </div>
                <div class="rp-worker-hours">{{ w.regularHours | number:'1.1-1' }} reg</div>
                <div class="rp-worker-cost">{{ w.totalCost | currency }}</div>
              </div>
            </div>
          </div>
        </ng-container>
      </div>
    </div>

  </ng-container>

</div>
